import { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { formatDate } from "@marekl/adminer/format";
import { parseApiDate } from "@marekl/adminer/datetime";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  DescriptionList,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Select,
  Table,
  type Column,
  Text,
} from "@marekl/adminer/components";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  useTeam,
  useMyTeams,
  useAssignTeamCmfId,
  useMoveTeamFederation,
  useRenameTeam,
  type TeamStatus,
  type TeamKind,
} from "../../../lib/queries/teams";
import { useFederations, type Federation } from "../../../lib/queries/federations";
import { useSetTeamPayment } from "../../../lib/queries/payments";
import { useUnregisterTeamSeason } from "../../../lib/queries/seasonRegistration";
import { hasNoSeasonRegistration, teamStateBadges } from "../../../lib/memberState";
import { useBreakpoint, useConfirm, useTableSort, type Breakpoint } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import { rosterGroup, sortRoster } from "../../../lib/rosterOrder";
import { BadgeRow } from "../../../components/BadgeRow";
import { RegisteredTick } from "../../../components/RegisteredTick";
import { DocumentConsentBadge } from "../../../components/DocumentConsentBadge";
import { useTeamSeasonDocuments } from "../../../lib/queries/seasonDocuments";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import {
  useRemoveMembership,
  useTeamMembers,
  type Membership,
  type Person,
} from "../../../lib/queries/memberships";
import { useSeason } from "../../../season";
import {
  useMe,
  hasAnyGlobalRole,
  agreesForTeam,
  canAdministerTeam,
  canReadAllMembers,
  readsTeamOnly,
} from "../../../auth/queries";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import { mayOpenSeasonRegistration } from "../../../lib/entryEligibility";
import { ROLE_LABELS, type IdentityLock } from "./memberFormSchema";
import { AddMemberModal } from "./AddMemberModal";
import { EditMemberModal } from "./EditMemberModal";
import { ChangeLeaderModal } from "./ChangeLeaderModal";
import { CreateLeaderLoginModal } from "./CreateLeaderLoginModal";
import { EditBillingModal } from "./EditBillingModal";
import { RoleCell } from "./RoleCell";
import { MemberPhoto } from "../../../components/MemberPhoto";
import { PhotoModal } from "./PhotoModal";
import { DocumentConsentCard } from "./DocumentConsentCard";
import { PersonLink } from "../../../components/PersonDetail";
import styles from "./TeamDetailPage.module.css";
import type { ID } from "../../../lib/id";

// Where the roster collapses into cards, stated once: the Table's `cards`
// prop and the photo chip that moves into the card's headline both read it,
// so the two cannot drift apart.
const ROSTER_CARDS: "sm" | "md" = "sm";
const CARD_BREAKPOINTS: Record<"sm" | "md", Breakpoint[]> = {
  sm: ["sm"],
  md: ["sm", "md"],
};

const COUNTRY_NAMES: Record<string, string> = Object.fromEntries(
  COUNTRY_OPTIONS.map((o) => [o.value, o.label]),
);

const STATUS_LABELS: Record<TeamStatus, string> = {
  pending: "Čeká na schválení",
  active: "Aktivní",
  suspended: "Pozastavený",
};

const KIND_LABELS: Record<TeamKind, string> = {
  full: "Tým",
  individual: "Individuální člen",
};

// EditTarget is what the single edit dialog is currently pointed at. Both
// the „Vedoucí" card and the roster rows produce one, so the leader is
// never edited through a different dialog than everyone else.
interface EditTarget {
  person: Person;
  membership: Membership | null;
  isLeader: boolean;
}

/** What SeasonRegistrationPage hands over when it redirects here. */
interface RegistrationFlash {
  season: string;
  created: number;
  updated: number;
  removed: number;
}

export function TeamDetailPage() {
  // Registrace do sezóny redirects here after a submit; the counts ride
  // along in router state so the confirmation lands where the leader now
  // is, instead of on the workspace they just left.
  const flash = (useLocation().state as { registration?: RegistrationFlash } | null)?.registration;
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = params.id ?? "";
  const { data: me } = useMe();
  // Deklarované nahoře, protože se na ně ptá i `enabled` u useFederations
  // níž. Jinak tam padne do dočasné mrtvé zóny.
  const isMembersAdmin = hasAnyGlobalRole(me, "admin", "members_admin");
  const { selectedSeason, activeSeason, isActiveSelected } = useSeason();
  // teamLoading matters as much as the data: until it lands, `team` is
  // undefined and every card below would otherwise state its empty case as
  // fact — „Bez vedoucího" for a team that has one.
  const { data: team, isLoading: teamLoading } = useTeam(
    teamId || undefined,
  );
  // Which federation the team belongs to decides what its roster can say:
  // a team outside ČNSMS holds Zahraniční členy, who can never be „Člen", so
  // the registration column and the kind switch mean nothing there.
  const teamIsCmf = team?.federation_is_primary ?? true;
  const { data: myTeams = [] } = useMyTeams();
  const { data: members = [], isLoading } = useTeamMembers(
    teamId || undefined,
    isActiveSelected ? undefined : selectedSeason?.id,
  );
  // The club's consents to the season's documents, for the state bar. Asked
  // only where the bar shows them: a registered ČNSMS club, active season.
  const seasonDocuments = useTeamSeasonDocuments(
    isActiveSelected && teamIsCmf && team?.status === "active" && (team?.members_registered ?? 0) > 0
      ? teamId
      : undefined,
  );
  const pendingDocuments = useMemo(
    () => (seasonDocuments.data ?? []).filter((d) => !d.agreed),
    [seasonDocuments.data],
  );
  const confirm = useConfirm();
  // Destructured: mutateAsync is a stable reference, so the memoised roster
  // columns do not re-sort when the mutation changes state.
  const { mutateAsync: removeMembership } = useRemoveMembership();
  const assignCmfId = useAssignTeamCmfId();
  const setPayment = useSetTeamPayment(teamId);
  const unregister = useUnregisterTeamSeason(teamId);
  const [addOpen, setAddOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const moveTeam = useMoveTeamFederation();
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTo, setRenameTo] = useState("");
  const renameTeam = useRenameTeam();
  // Načítá se jen adminovi, a jen na klubu mimo ČNSMS: jinak je to dotaz
  // za seznam, který nikdo neuvidí. Ta podmínka tu dřív jen stála v
  // komentáři: bez `enabled` se dotaz posílal vždycky, a `GET /federations`
  // vrací 403 každému, kdo není centrální ani federační správce. Knihovna
  // zkracuje jen 401, takže každý vedoucí klubu na tom při otevření svého
  // týmu vystřílel několik zbytečných odmítnutí. Stejná podmínka, za kterou
  // se ukazuje jediný spotřebitel (tlačítko „Přesunout tým do jiné
  // federace"), aby se ty dvě nemohly rozejít.
  const { data: federations = [] } = useFederations({
    enabled: isMembersAdmin && team?.federation_is_primary === false,
  });
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [leaderOpen, setLeaderOpen] = useState(false);
  const [leaderLoginOpen, setLeaderLoginOpen] = useState(false);
  const [photoFor, setPhotoFor] = useState<Person | null>(null);
  // Per-person cache-buster. The photo URL never changes, so a replaced
  // photo would otherwise keep showing the bytes the browser already has.
  const [photoVersion, setPhotoVersion] = useState<Record<ID, number>>({});

  // Season/registration gating. Writes only ever target the active
  // season; a non-active selection is a read-only historical view.
  const registrationOpen = !!activeSeason?.registration_open;
  // Who can change the roster right now: only while viewing the active
  // season, and only when registration is open or you are a membership
  // admin (mirrors the backend gate).
  // The window freezes the MČR soupiska, so it binds only where one can
  // exist: a ČNSMS club. A club outside ČNSMS holds Zahraniční členy, whom the
  // server lets in year-round (requireRegistrationOpen returns nil for any
  // kind but registered), so gating its roster on ČNSMS's dates locked a club
  // out of its own soupiska over a deadline that was never its own.
  const windowBinds = teamIsCmf && !registrationOpen && !isMembersAdmin;
  // Čtení členské základny reaches every team and writes none, so for it
  // the active season reads like a past one: everything shown, nothing
  // offered. `isWritable` is the page's single „may I offer a write here".
  const readOnly = readsTeamOnly(me, team);
  const isWritable = isActiveSelected && !readOnly;
  const canMutateRoster = isWritable && !windowBinds;
  // Guests are exempt for the same reason, one row at a time: pohárové
  // soutěže run year-round and a guest is not on the soupiska the window
  // freezes. So a row's mutability depends on the row, not just on the page.
  // useCallback because the roster columns are memoised: an identity that
  // changed every render would re-sort the table on every render.
  const canMutateRow = useCallback(
    (m: Membership) => (m.kind === "registered" ? canMutateRoster : isWritable),
    [isWritable, canMutateRoster],
  );
  // The registration workspace link is shown under the same rule — and
  // never for a team outside ČNSMS, whose submit the server refuses
  // (ErrSeasonRegistrationNotForTeam): a door into a room with no exit.
  const showRegistrationLink =
    isWritable && teamIsCmf && mayOpenSeasonRegistration(activeSeason, me);
  // „Zpět na členskou základnu" leads somewhere only when there is a
  // choice to go back to: /clenska-zakladna resolves to the team picker,
  // which redirects straight back here for a leader with a single active
  // team (MembershipPage). Same condition, so the link is never a no-op.
  const showBackToRegistry =
    canReadAllMembers(me) || myTeams.filter((t) => t.status === "active").length > 1;
  // Why a person's identity is restricted here, mirroring the backend rules
  // in membership.UpdatePerson. „filled" is the ordinary case: with the window
  // open a leader may complete a missing rodné číslo but never rewrite a name
  // or a birth date that is already on file (ErrPersonIdentityImmutable) —
  // the same per-field rule „Registrace do sezóny" applies in its grid.
  const identityLockFor = (isLeader: boolean, kind?: Membership["kind"]): IdentityLock => {
    if (isMembersAdmin) return null;
    if (isLeader) return "leader";
    // The lock exists to freeze an MČR soupiska, and neither a nečlen nor
    // a člen of a club outside ČNSMS is ever on one. The server says the same
    // by asking for a REGISTERED membership (membership.isRegisteredMember);
    // locking a field here that the server would happily write is a form
    // that refuses an edit nobody refused.
    if (kind === "guest" || kind === "foreign") return null;
    if (isActiveSelected && !registrationOpen) return "closed";
    return "filled";
  };
  // Unknown while the seasons load: the sentence then just drops the code.
  const seasonCode = selectedSeason?.code;

  // Condensed team state (active-season). notRegistered = no members yet,
  // which also disables the payment toggle (nothing to pay for).
  const stateIssues = team?.state_issues ?? [];
  const notRegistered = hasNoSeasonRegistration(stateIssues);

  // Which kind „Přidat …" creates, and therefore which fields it asks for.
  //
  // A club outside ČNSMS holds Zahraniční členy and nothing else. A ČNSMS club
  // that has not registered into the season cannot hold a Člen at all
  // (ErrTeamNotRegistered), so its dialog creates Nečleny for pohárové
  // soutěže and never asks for a rodné číslo. Registering the club is one
  // deliberate press on „Registrace do sezóny", not a side effect of this
  // form.
  const addKind: Membership["kind"] = !teamIsCmf
    ? "foreign"
    : notRegistered
      ? "guest"
      : "registered";
  // Zpětný chod k „Registrace do sezóny", a proto pod stejnou podmínkou:
  // kdo smí registraci podat, smí ji do uzávěrky i vzít zpět. Registrační
  // okno tu ale platí i pro správce členské základny, na rozdíl od zbytku
  // soupisky: po uzávěrce se členský příspěvek vyúčtuje.
  const showUnregister =
    showRegistrationLink && !notRegistered && registrationOpen;
  // What the club is told about its season, and what it is offered.
  //
  //   „cup_only"       — it has never been a ČNSMS member, or chose not to be.
  //                      A finished state, so the card explains it and offers
  //                      the way in rather than nagging.
  //   „not_registered" — it WAS a member and the window is open. Two real
  //                      paths, both legitimate: register, or carry on with
  //                      pohárovky. Doing nothing is the second one, which is
  //                      why the card names its consequence instead of
  //                      flashing red.
  const showSeasonChoice = showRegistrationLink && notRegistered && registrationOpen;
  const wasMemberBefore = stateIssues.includes("not_registered");
  // The card says it better than a badge would, so the badge does not repeat
  // it. „Chybí registrace" in red also says it WRONGLY here: membership is
  // seasonal and voluntary, and a club that has not renewed owes nobody
  // anything. The red stays on the admin list, which is a chasing tool read
  // by somebody else. With nothing else outstanding the row disappears
  // entirely rather than falling through to „Připraveno", which would be a
  // verdict on machinery that has not run.
  // Date-only on the wire, so it stays a string and needs parseApiDate.
  const registrationClosesOn = activeSeason?.registration_open_to
    ? formatDate(parseApiDate(activeSeason.registration_open_to))
    : null;
  const badgeIssues = showSeasonChoice
    ? stateIssues.filter((i) => i !== "not_registered" && i !== "cup_only")
    : stateIssues;
  const showStateBadges = !showSeasonChoice || badgeIssues.length > 0;
  // With the card above carrying the season and nothing else outstanding,
  // the strip would be the words „Stav týmu" and empty space. A region is
  // gated, not emptied.
  const isPaid = team?.payment_status === "paid";
  // The fee is settled after the uzávěrka; until then the club may still
  // leave the season (season.FeeDue, decided by the server). A payment
  // already on file stays undoable.
  const showPaymentToggle = isMembersAdmin && (!!activeSeason?.fee_due || isPaid);
  const showStateBar =
    showStateBadges || (team?.members_registered ?? 0) > 0 || showPaymentToggle || showUnregister;
  const membersRegistered = team?.members_registered ?? 0;

  // Leadership and season membership are two records for one human. The
  // card and that person's roster row therefore resolve to the same edit
  // target; leaderMembership is null only when the leader has no roster row
  // for the selected season yet.
  const leaderPersonId = team?.leader?.person.id;
  const leaderMembership = members.find((m) => m.person.id === leaderPersonId) ?? null;
  const isLeaderRow = useCallback(
    (m: Membership) => m.person.id === leaderPersonId,
    [leaderPersonId],
  );
  // The roster arrives in insertion order, which reads as no order at all.
  // Vedoucí first, then trenéři and asistenti, then the rest — each block by
  // surname in Czech collation.
  const roster = useMemo(() => sortRoster(members, leaderPersonId), [members, leaderPersonId]);
  const leaderTarget: EditTarget | null = team?.leader
    ? {
        // Both sides are the same Person record; prefer the roster row's
        // copy so the dialog and the table can't drift.
        person: leaderMembership?.person ?? team.leader.person,
        membership: leaderMembership,
        isLeader: true,
      }
    : null;

  // useCallback because the roster columns are memoised: an identity that
  // changes every render would re-sort the table on every render.
  const askRemove = useCallback(
    (m: Membership) =>
      confirm({
        title: "Odebrat člena",
        // The server refuses this when a přihláška names the person, and the
        // refusal is a sentence listing the soutěže it found. Passing the work
        // as onConfirm is what keeps that on screen: the dialog stays open and
        // shows it, instead of closing and leaving only a toast.
        confirmLabel: "Odebrat",
        variant: "danger",
        children: (
          <>
            <p>
              Opravdu odebrat člena{" "}
              <strong>
                {m.person.first_name} {m.person.last_name}
              </strong>{" "}
              ze sezóny <strong>{m.season_code}</strong>?
            </p>
            <p>
              Členství zůstane v historii zachované, jen bude označené jako ukončené
              k dnešnímu dni.
            </p>
          </>
        ),
        onConfirm: () => removeMembership(m.id),
      }),
    [confirm, removeMembership],
  );

  // Zrušení registrace do sezóny. Server odmítá, dokud klub drží živou
  // přihlášku na MČR, a odmítnutí ty soutěže vyjmenuje: proto jde práce do
  // onConfirm, aby ta věta zůstala v dialogu u tlačítka, které ji vyvolalo.
  function onUnregisterSeason() {
    confirm({
      title: "Zrušit registraci do sezóny",
      confirmLabel: "Zrušit registraci",
      variant: "danger",
      children: (
        <>
          <p>
            Opravdu zrušit registraci týmu <strong>{team?.name}</strong> do sezóny{" "}
            <strong>{activeSeason?.code}</strong>?
          </p>
          <p>
            Všichni členové na soupisce se stanou neregistrovanými. Zůstanou na
            soupisce a mohou dál na pohárové soutěže; na MČR už tým přihlášku
            podat nemůže.
          </p>
          <p>
            Přidělená ČNSMS ID zůstávají osobám i týmu zachovaná. Registraci
            můžete znovu podat na stránce Registrace do sezóny, a to až do
            uzávěrky.
          </p>
        </>
      ),
      onConfirm: async () => {
        await unregister.mutateAsync();
      },
    });
  }

  async function onAssignCmfId() {
    if (!team) return;
    try {
      await assignCmfId.mutateAsync(team.id);
    } catch (err) {
      // Reported here rather than by the global handler: the hook opts out of
      // it for TeamsPage, where the failure lands in a confirm dialog. This
      // button has no dialog, so without this the refusal is invisible.
      notifyError(err);
    }
  }

  const rosterIsCards = CARD_BREAKPOINTS[ROSTER_CARDS].includes(useBreakpoint());

  // Hoisted for useTableSort, which re-sorts on the columns identity.
  const rosterColumns = useMemo<Column<Membership>[]>(() => {
    const photoChip = (m: Membership, size?: number) => (
      <MemberPhoto
        personId={m.person.id}
        firstName={m.person.first_name}
        lastName={m.person.last_name}
        hasPhoto={!!m.person.photo_uploaded}
        version={photoVersion[m.person.id] ?? 0}
        size={size}
        // A nečlen never appears on an MČR soupiska, so a photo is
        // not something they owe — same rule EditMemberModal
        // applies when it hides the upload field for a guest.
        onAdd={
          isWritable && m.kind === "registered"
            ? () => setPhotoFor(m.person)
            : undefined
        }
      />
    );
    return [
            {
              key: "photo",
              header: "Foto",
              cardHidden: true,
              render: (m) => photoChip(m),
            },
            {
              key: "name",
              header: "Jméno",
              primary: true,
              sortable: true,
              // By surname, the way a Czech roster is read and the way
              // sortRoster already orders it — the cell shows "Eva Nová"
              // but nobody looks for her under E.
              sortValue: (m) => `${m.person.last_name} ${m.person.first_name}`,
              // Admins open the person detail dialog; for a leader this is the
              // plain name (the dialog reads the whole registry).
              // On a phone the „Foto" column is cardHidden, so the chip
              // rides in the card's headline instead: a leader must see who
              // still owes a photo, and add it, without opening „Upravit".
              // 44px is the touch target. A nečlen owes no photo, so one
              // without gets an initials placeholder, not the dashed
              // „Chybí foto" circle: beside the name it would read as a debt.
              render: (m) => (
                <span className={styles.nameCell}>
                  {rosterIsCards &&
                    (m.kind === "registered" || m.person.photo_uploaded ? (
                      photoChip(m, 44)
                    ) : (
                      <Avatar
                        className={styles.placeholder}
                        name={`${m.person.first_name} ${m.person.last_name}`}
                      />
                    ))}
                  <PersonLink personId={m.person.id}>
                    {m.person.first_name} {m.person.last_name}
                  </PersonLink>
                </span>
              ),
            },
            // A ČNSMS ID is handed out with a season registration, which a
            // club outside ČNSMS never makes. Same reason as „Registrovaný"
            // below: the column could only ever be a stripe of dashes.
            ...(teamIsCmf
              ? [
                  {
                    key: "cmf_id",
                    header: "ČNSMS ID",
                    sortable: true,
                    // The dash is a placeholder, not a value: null sorts last both ways.
                    sortValue: (m: Membership) => m.person.cmf_id ?? null,
                    render: (m: Membership) => m.person.cmf_id ?? "—",
                  },
                ]
              : []),
            {
              key: "birth_date",
              header: "Datum narození",
              sortable: true,
              // SORTED on the raw date-only ISO string, because
              // lexicographic order is chronological there; RENDERED in
              // Czech, like every other date on screen. The two differ on
              // purpose: „14.09.2010" sorts by day before month.
              sortValue: (m) => m.person.birth_date,
              render: (m) =>
                m.person.birth_date ? formatDate(m.person.birth_date) : "—",
            },
            // „Registrovaný" answers a ČNSMS question. On a team outside
            // ČNSMS nobody can hold that kind, so the column would be an
            // empty stripe down the whole roster.
            ...(teamIsCmf
              ? [
                  {
                    key: "membership_kind",
                    header: "Registrovaný",
                    sortable: true,
                    // What the cell reads is a tick or an empty space, so
                    // the sort is over presence. Not null for a guest:
                    // null sorts last in BOTH directions, and "who is not
                    // registered yet" is exactly the question this column
                    // gets sorted for.
                    sortValue: (m: Membership) => (m.kind === "registered" ? 1 : 0),
                    render: (m: Membership) => (
                      <RegisteredTick registered={m.kind === "registered"} />
                    ),
                  },
                ]
              : []),
            {
              key: "roles",
              header: "Role",
              sortable: true,
              // The same rank the roster opens in — vedoucí, then trenéři
              // and asistenti, then everyone else. Reused from rosterOrder
              // rather than restated, so the column cannot drift from the
              // default order it reproduces. Rows inside one rank keep
              // their incoming surname order: the sort is stable.
              sortValue: (m) => rosterGroup(m, leaderPersonId),
              render: (m) => <RoleCell roles={m.roles} isLeader={isLeaderRow(m)} />,
            },
            ...(isWritable
              ? [
                  {
                    key: "actions",
                    header: "",
                    render: (m: Membership) => (
                      <Table.Actions>
                        <Table.Action
                          appearance="button"
                          onClick={() =>
                            setEditing({
                              person: m.person,
                              membership: m,
                              isLeader: isLeaderRow(m),
                            })
                          }
                        >
                          Upravit
                        </Table.Action>
                        {/* The leader stays on the soupiska (the backend
                            rejects removing them) — clearing their roles
                            in the dialog is the way to make them „pouze
                            vedoucí". */}
                        {canMutateRow(m) && !isLeaderRow(m) && (
                          <Table.Action appearance="button" variant="danger" onClick={() => askRemove(m)}>
                            Odebrat
                          </Table.Action>
                        )}
                      </Table.Actions>
                    ),
                  },
                ]
              : []),
    ];
  }, [
      rosterIsCards,
      isWritable,
      photoVersion,
      canMutateRow,
      isLeaderRow,
      leaderPersonId,
      askRemove,
      teamIsCmf,
  ]);

  // No defaultSort on purpose. Unsorted means sortRoster's order —
  // leader, then coaches and assistants, then everyone else by surname —
  // which no single column can express. Clicking a header sorts by that
  // column; Back (or a URL without ?sort=) returns to the grouping.
  const rosterSort = useTableSort(roster, {
    columns: rosterColumns,
    searchParams,
    setSearchParams,
  });

  return (
    <>
      <AppTopbar
        seasonSwitcher="readonly"
        crumbs={[{ label: "Týmy", href: "/admin/tymy" }, { label: team?.name ?? "Detail týmu" }]}
      />
      <Page>
        <Page.Header
          title={team?.name ?? "Detail týmu"}
          subtitle={
            team
              ? [
                  KIND_LABELS[team.kind],
                  STATUS_LABELS[team.status],
                  // Jen u klubu mimo ČNSMS: „pod ČNSMS" je u české soupisky
                  // výchozí stav a psát ho ke každému týmu je šum. Tady
                  // navíc říká, co přesně mění tlačítko vedle.
                  team.federation_is_primary === false
                    ? team.federation_short_name || team.federation_name
                    : undefined,
                  team.cmf_id ? `ČNSMS ID ${team.cmf_id}` : undefined,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : undefined
          }
          actions={
            <>
              {/* ČNSMS ID allocation is a members_admin action (the backend
                  gates the endpoint the same way) — a leader would only
                  get a 403 out of this button. */}
              {isMembersAdmin && isActiveSelected && team && team.status === "active" &&
                teamIsCmf && team.kind === "full" &&
                (!team.cmf_id || (team.members_missing_cmf_id ?? 0) > 0) && (
                <Button
                  loading={assignCmfId.isPending}
                  onClick={onAssignCmfId}
                >
                  {team.cmf_id
                    ? `Doplnit ČNSMS ID členům (${team.members_missing_cmf_id ?? 0})`
                    : "Přidělit ČNSMS ID"}
                </Button>
              )}
              {/* The name is the federation's, not the club's: the same
                  gate as approving and suspending (rbac.CanAdministerTeam).
                  Not season data, so it stays offered on a past season. */}
              {team && canAdministerTeam(me, team) && (
                <Button
                  onClick={() => {
                    setRenameTo(team.name);
                    setRenameOpen(true);
                  }}
                >
                  Přejmenovat tým
                </Button>
              )}
              {/* Tým přechází mezi federacemi jen mimo ČNSMS: na každé
                  straně té čáry znamená soupiska něco jiného. Přesouvá
                  ho ústřední správa, takže Správci federace se dialog,
                  který skončí 403, nenabízí. Sedí na detailu, protože
                  je to úkon nad jedním týmem, a tady je vidět, z čeho
                  se přesouvá. */}
              {isMembersAdmin && team?.federation_is_primary === false && (
                <Button
                  onClick={() => {
                    setMoveTarget("");
                    setMoveOpen(true);
                  }}
                >
                  Přesunout do federace
                </Button>
              )}
              {showRegistrationLink && (
                <Link to={`/teams/${teamId}/registrace`}>
                  <Button>Registrace do sezóny</Button>
                </Link>
              )}
              {/* The window freezes the MČR soupiska, so it binds only the
                  kind that is on one. A nečlen is exempt server-side
                  (requireRegistrationOpen returns nil for every other kind),
                  which is what canMutateRow already encodes per row — so a
                  cup-only club kept adding the nečleny its year-round
                  pohárovky run on. */}
              {(addKind === "registered" ? canMutateRoster : isWritable) && (
                <Button onClick={() => setAddOpen(true)}>
                  {addKind === "registered"
                    ? "Přidat člena"
                    : addKind === "guest"
                      ? "Přidat neregistrovaného člena"
                      : "Přidat zahraničního člena"}
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          {flash && (
            <Alert variant="ok" title={`Registrace do sezóny ${flash.season} odeslána`}>
              Vytvořeno {flash.created}, upraveno {flash.updated}, odebráno {flash.removed}.
            </Alert>
          )}

          {readOnly && (
            <Alert variant="info" title="Pouze pro čtení">
              Máte roli {ROLE_LABEL.members_viewer}: tým a jeho soupisku vidíte,
              ale nic na nich neměníte.
            </Alert>
          )}
          {!isActiveSelected && (
            <Alert variant="info" title="Historická sezóna">
              Prohlížíte sezónu <strong>{selectedSeason?.code}</strong>. Data jsou
              pouze pro čtení; změny lze provádět jen v aktivní sezóně
              {activeSeason ? ` (${activeSeason.code})` : ""}.
            </Alert>
          )}
          {isWritable && windowBinds && (
            <Alert
              variant="warn"
              title={
                activeSeason?.phase === "pre_registration"
                  ? "Registrace ještě nezačala"
                  : "Registrace uzavřena"
              }
            >
              Registrace pro sezónu <strong>{activeSeason?.code}</strong>{" "}
              {activeSeason?.phase === "pre_registration"
                ? "zatím nezačala"
                : "je uzavřena"}
              . Změny soupisky nyní provádí správce členské základny.
            </Alert>
          )}

          {/* Co se s klubem v téhle sezóně děje, a co s tím může udělat.
              „Chybí registrace" červeně tady nebylo na místě: členství je
              sezónní a dobrovolné, klub, který ho neobnovil, nikomu nic nedluží.
              Obě cesty jsou legitimní a nicnedělání je ta druhá, takže se
              pojmenuje i s důsledkem, místo aby se na klub blikalo. */}
          {showSeasonChoice && (
            <Alert
              variant="info"
              title={
                wasMemberBefore
                  ? `Registrace do sezóny ${activeSeason?.code} je otevřená`
                  : "Tým se účastní jen pohárových soutěží"
              }
            >
              <div className={styles.seasonChoice}>
                <p>
                  {wasMemberBefore ? (
                    <>
                      V minulé sezóně byl tým členem ČNSMS. Pro sezónu{" "}
                      <strong>{activeSeason?.code}</strong> se zatím
                      nezaregistroval. Registrace je otevřená
                      {registrationClosesOn ? ` do ${registrationClosesOn}` : ""}.
                    </>
                  ) : (
                    <>
                      Tým není členem ČNSMS pro sezónu{" "}
                      <strong>{activeSeason?.code}</strong>. Na pohárové soutěže
                      to stačí: přidávejte neregistrované členy a přihlašujte je.
                      Na postupové soutěže ČNSMS je potřeba registrace
                      {registrationClosesOn ? `, a to do ${registrationClosesOn}` : ""}.
                    </>
                  )}
                </p>
                <p className={styles.seasonChoiceHint}>
                  Pokud se tým registrovat nechce, nemusíte dělat nic. Po
                  uzávěrce zůstane v režimu „Jen pohárovky“, členy můžete dál
                  přihlašovat na pohárové soutěže, historie zůstává zachovaná a
                  zaregistrovat se můžete znovu příští sezónu.
                </p>
                <Link to={`/teams/${teamId}/registrace`}>
                  <Button variant="primary">Registrovat tým do sezóny</Button>
                </Link>
              </div>
            </Alert>
          )}

          {/* Registrace, členský příspěvek, fotky: celý pruh odpovídá na
              otázky ČNSMS, a tým mimo ni žádnou z nich nedluží (server pro něj
              `state_issues` ani neplní). */}
          {isActiveSelected && team && team.status === "active" && teamIsCmf && showStateBar && (
            <Card>
              <Card.Content>
                <div className={styles.stateBar}>
                  <BadgeRow gap="md">
                    <Text variant="mono">Stav týmu</Text>
                    {showStateBadges && teamStateBadges(badgeIssues).map((b) => (
                      <Badge key={b.label} variant={b.variant}>
                        {b.label}
                      </Badge>
                    ))}
                    {membersRegistered > 0 && (
                      <Badge variant={team.members_with_photo === membersRegistered ? "ok" : "muted"}>
                        {team.members_with_photo ?? 0}/{membersRegistered} fotek
                      </Badge>
                    )}
                    {/* Consent is given by registering, so a club that has
                        not registered owes none yet. */}
                    {membersRegistered > 0 && (
                      <DocumentConsentBadge documents={seasonDocuments.data ?? []} />
                    )}
                  </BadgeRow>
                  <BadgeRow gap="md">
                    {/* Členství v sezóně se dá do uzávěrky vzít zpět. Klub,
                        který se rozmyslel, tak nemusí táhnout registraci, za
                        kterou by platil; po uzávěrce se příspěvek vyúčtuje a
                        tlačítko mizí všem, správce členské základny nevyjímaje
                        (ErrUnregisterWindowClosed). */}
                    {showUnregister && (
                      <Button
                        weight="outline"
                        size="sm"
                        loading={unregister.isPending}
                        onClick={onUnregisterSeason}
                      >
                        Zrušit registraci do sezóny
                      </Button>
                    )}
                    {showPaymentToggle && (
                      <Button
                        // `warning` while unpaid: the button names the OUTSTANDING
                        // state, not the happy one it produces. „Nezaplaceno" is
                        // something to chase, and the badge beside it says so in the
                        // same colour. Neutral once paid — undoing a payment is not
                        // a positive act either.
                        variant={isPaid ? "neutral" : "warning"}
                        size="sm"
                        loading={setPayment.isPending}
                        disabled={notRegistered}
                        title={
                          !notRegistered
                            ? undefined
                            : activeSeason
                              ? `Tým nemá v sezóně ${activeSeason.code} registrované členy`
                              : "Tým nemá registrované členy"
                        }
                        onClick={() => setPayment.mutate({ status: isPaid ? "unpaid" : "paid" })}
                      >
                        {isPaid ? "Označit jako nezaplaceno" : "Označit jako zaplaceno"}
                      </Button>
                    )}
                  </BadgeRow>
                </div>
              </Card.Content>
            </Card>
          )}

          {/* Consent is given by registering, so a club that has not
              registered owes none; the query is already gated the same way. */}
          {isWritable && (team?.members_registered ?? 0) > 0 && pendingDocuments.length > 0 && (
            <DocumentConsentCard
              teamId={teamId}
              documents={pendingDocuments}
              agrees={agreesForTeam(me, teamId)}
              leaderHasLogin={!!team?.leader?.login_email}
            />
          )}

          <ResponsiveGrid columns={2} gap="lg">
            {/* Body-only: the header keeps its title and its „Změnit
                vedoucího" button, which need no data to be correct. */}
            <Card>
              <Card.Header
                title="Vedoucí"
                actions={
                  leaderTarget && isWritable ? (
                    <>
                      {/* Handing the team on changes who may administer it, so
                          it is a federation decision — members_admin and up,
                          never the leader themselves (the backend agrees). */}
                      {isMembersAdmin && (
                        <Button size="sm" onClick={() => setLeaderOpen(true)}>
                          Změnit vedoucího
                        </Button>
                      )}
                      <Button variant="primary" size="sm" onClick={() => setEditing(leaderTarget)}>
                        Upravit
                      </Button>
                    </>
                  ) : undefined
                }
              />
              <Card.Content isLoading={teamLoading}>
                {team?.leader ? (
                  <DescriptionList labelWidth={160}>
                    <DescriptionList.Item label="Jméno">
                      {team.leader.person.first_name} {team.leader.person.last_name}
                    </DescriptionList.Item>
                    {teamIsCmf && (
                      <DescriptionList.Item label="ČNSMS ID">
                        {team.leader.person.cmf_id ? (
                          team.leader.person.cmf_id
                        ) : (
                          <Text variant="muted">—</Text>
                        )}
                      </DescriptionList.Item>
                    )}
                    <DescriptionList.Item label="Datum narození">
                      {team.leader.person.birth_date ? (
                        formatDate(team.leader.person.birth_date)
                      ) : (
                        <Text variant="muted">—</Text>
                      )}
                    </DescriptionList.Item>
                    {/* The leader's only e-mail is their login address; persons
                        carry none of their own. Changing it is a user-account
                        operation, not a Person edit — hence read-only here. */}
                    <DescriptionList.Item label="Přihlašovací e-mail">
                      {team.leader.login_email ? (
                        team.leader.login_email
                      ) : (
                        // A leader with no account is a team nobody can
                        // administer, so the gap is named and, for those who
                        // may hand a team over, fixable right here.
                        <BadgeRow gap="sm">
                          <Text variant="muted">Bez účtu</Text>
                          {isMembersAdmin && isActiveSelected && (
                            <Button size="sm" onClick={() => setLeaderLoginOpen(true)}>
                              Vytvořit účet
                            </Button>
                          )}
                        </BadgeRow>
                      )}
                    </DescriptionList.Item>
                    <DescriptionList.Item label="Telefon">
                      {team.leader.person.phone || <Text variant="muted">—</Text>}
                    </DescriptionList.Item>
                    <DescriptionList.Item label="Vede od">
                      {formatDate(team.leader.valid_from)}
                    </DescriptionList.Item>
                    <DescriptionList.Item label="Role v týmu">
                      {leaderMembership?.roles.length ? (
                        leaderMembership.roles.map((r) => ROLE_LABELS[r]).join(", ")
                      ) : (
                        <Text variant="muted">Pouze vedoucí</Text>
                      )}
                    </DescriptionList.Item>
                  </DescriptionList>
                ) : (
                  <EmptyState
                    title="Bez vedoucího"
                    description="Tým aktuálně nemá přiřazeného vedoucího."
                    action={
                      // The card header's actions hang off leaderTarget, which a
                      // team with no leader does not have — so without this the one
                      // team that most needs a leader is the one that cannot be
                      // given one.
                      isMembersAdmin && isActiveSelected ? (
                        <Button variant="primary" onClick={() => setLeaderOpen(true)}>
                          Přiřadit vedoucího
                        </Button>
                      ) : undefined
                    }
                  />
                )}
              </Card.Content>
            </Card>

            <Card>
              <Card.Header
                title="Fakturační údaje"
                actions={
                  isWritable ? (
                    <Button variant="primary" size="sm" onClick={() => setBillingOpen(true)}>
                      {team?.billing_info ? "Upravit" : "Doplnit"}
                    </Button>
                  ) : undefined
                }
              />
              <Card.Content isLoading={teamLoading}>
                {team?.billing_info ? (
                  <DescriptionList labelWidth={160}>
                    <DescriptionList.Item label="Kontaktní osoba">
                      {team.billing_info.contact_name}
                    </DescriptionList.Item>
                    {team.billing_info.subject_name && (
                      <DescriptionList.Item label="Subjekt">
                        {team.billing_info.subject_name}
                      </DescriptionList.Item>
                    )}
                    <DescriptionList.Item label="E-mail">
                      {team.billing_info.contact_email || <Text variant="muted">—</Text>}
                    </DescriptionList.Item>
                    <DescriptionList.Item label="Telefon">
                      {team.billing_info.contact_phone || <Text variant="muted">—</Text>}
                    </DescriptionList.Item>
                    <DescriptionList.Item label="Adresa">
                      {team.billing_info.street}, {team.billing_info.postal_code} {team.billing_info.city},{" "}
                      {COUNTRY_NAMES[team.billing_info.country_code] ?? team.billing_info.country_code}
                    </DescriptionList.Item>
                    {(team.billing_info.ico || team.billing_info.dic) && (
                      <DescriptionList.Item label="IČO / DIČ">
                        {[team.billing_info.ico, team.billing_info.dic].filter(Boolean).join(" / ") || (
                          <Text variant="muted">—</Text>
                        )}
                      </DescriptionList.Item>
                    )}
                  </DescriptionList>
                ) : (
                  <EmptyState
                    title="Bez fakturačních údajů"
                    description="Tým ještě nemá vyplněné fakturační údaje."
                  />
                )}
              </Card.Content>
            </Card>
          </ResponsiveGrid>

          <Page.SectionHeader title={seasonCode ? `Členové sezóny ${seasonCode}` : "Členové"} />
          <Table<Membership>
            framed
            {...rosterSort.tableProps}
            rowKey={(m) => m.id}
            columns={rosterColumns}
            isLoading={isLoading}
            cards={ROSTER_CARDS}
            empty={
              <EmptyState
                title="Žádní členové"
                description={
                  seasonCode
                    ? `Tým nemá evidované členy pro sezónu ${seasonCode}.`
                    : "Tým nemá evidované členy."
                }
                action={
                  showRegistrationLink ? (
                    <Link to={`/teams/${teamId}/registrace`}>
                      <Button variant="primary">Registrace do sezóny</Button>
                    </Link>
                  ) : undefined
                }
              />
            }
          />

          {showBackToRegistry && (
            <p>
              <Link to="/clenska-zakladna">Zpět na členskou základnu</Link>
            </p>
          )}
        </Page.Content>
      </Page>

      {/* Mounted on open, like every other dialog on this page. Mounted
          always, useForm captured its defaultValues on the FIRST render —
          when `team` is still undefined, so addKind reads „registered" — and
          never revised them. The dialog then hid the ČNSMS fields (which follow
          the prop) while still validating and posting as a člen: „Uložit" did
          nothing visible, and had it submitted the server would have refused
          it with ErrTeamNotRegistered. */}
      {addOpen && (
        <AddMemberModal
          teamId={teamId}
          kind={addKind}
          open
          onClose={() => setAddOpen(false)}
        />
      )}

      <FormModal
        open={renameOpen}
        title="Přejmenovat tým"
        submitLabel="Přejmenovat"
        busy={renameTeam.isPending}
        canSubmit={renameTo.trim() !== "" && renameTo.trim() !== team?.name}
        error={renameTeam.error}
        onClose={() => {
          setRenameOpen(false);
          renameTeam.reset();
        }}
        onSubmit={() => {
          renameTeam.mutate(
            { id: teamId, name: renameTo.trim() },
            { onSuccess: () => setRenameOpen(false) },
          );
        }}
      >
        <Input
          label="Název týmu"
          required
          value={renameTo}
          onChange={(e) => setRenameTo(e.target.value)}
          hint="Nový název se hned ukáže všude, i na zveřejněných startovních listinách a v exportech."
        />
      </FormModal>
      <FormModal
        open={moveOpen}
        title="Přesunout tým do jiné federace"
        submitLabel="Přesunout"
        busy={moveTeam.isPending}
        canSubmit={moveTarget !== ""}
        error={moveTeam.error}
        onClose={() => {
          setMoveOpen(false);
          moveTeam.reset();
        }}
        onSubmit={() => {
          moveTeam.mutate(
            { id: teamId, federation_id: moveTarget },
            { onSuccess: () => setMoveOpen(false) },
          );
        }}
      >
        <p>
          Tým <strong>{team?.name}</strong> je teď pod federací{" "}
          <strong>{team?.federation_name ?? "—"}</strong>.
        </p>
        <Select
          label="Nová federace"
          required
          value={moveTarget}
          onChange={(e) => setMoveTarget(e.target.value)}
          hint="Do ČNSMS ani z ČNSMS tým přesunout nelze: na každé straně znamená soupiska něco jiného."
        >
          <option value="">Vyberte federaci</option>
          {federations
            .filter((f: Federation) => !f.is_primary && f.id !== team?.federation_id)
            .map((f: Federation) => (
              <option key={f.id} value={String(f.id)}>
                {f.name}
              </option>
            ))}
        </Select>
      </FormModal>
      {photoFor && (
        <PhotoModal
          teamId={teamId}
          person={photoFor}
          onClose={() => {
            setPhotoVersion((v) => ({ ...v, [photoFor.id]: (v[photoFor.id] ?? 0) + 1 }));
            setPhotoFor(null);
          }}
        />
      )}

      {editing && (
        <EditMemberModal
          teamId={teamId}
          person={editing.person}
          membership={editing.membership}
          isLeader={editing.isLeader}
          open
          onClose={() => setEditing(null)}
          identityLock={identityLockFor(editing.isLeader, editing.membership?.kind)}
          lockRoles={editing.membership ? !canMutateRow(editing.membership) : !canMutateRoster}
          hideKind={!isMembersAdmin || !teamIsCmf}
        />
      )}
      {/* Mounted only while open: it loads the whole soupiska, which is
          wasted work on every team-detail render — and a copy that goes stale
          the moment anyone edits the roster behind it. */}
      {leaderOpen && (
        <ChangeLeaderModal
          teamId={teamId}
          currentLeaderPersonId={team?.leader?.person.id}
          leaderKind={addKind}
          open
          onClose={() => setLeaderOpen(false)}
        />
      )}
      {leaderLoginOpen && team?.leader && (
        <CreateLeaderLoginModal
          teamId={teamId}
          leaderName={`${team.leader.person.first_name} ${team.leader.person.last_name}`}
          open
          onClose={() => setLeaderLoginOpen(false)}
        />
      )}
      {team && (
        <EditBillingModal
          teamId={team.id}
          billing={team.billing_info}
          open={billingOpen}
          onClose={() => setBillingOpen(false)}
        />
      )}
    </>
  );
}
